import { createAsyncThunk } from "@reduxjs/toolkit";
import { authService } from "@/services/auth-service";
import { loginStart, loginSuccess, loginFailure } from "./auth-slice";
import { AxiosError, AxiosResponse } from "axios";
import Cookies from "js-cookie";
import posthog from "posthog-js";
import { fetchUser } from "./user-slice";
import { fetchRoles } from "./roles-slice";

interface LoginResponse {
  message: string;
  access_token: string;
  refresh_token: string;
}

interface LoginCredentials {
  email: string;
  password: string;
}

/** Use after password login when the API returns tokens without calling `/api/auth/login` again. */
export const establishSessionWithTokens = createAsyncThunk(
  "auth/establishSessionWithTokens",
  async (
    tokens: { access_token: string; refresh_token: string },
    { dispatch, rejectWithValue }
  ) => {
    dispatch(loginStart());
    const loginPayload = {
      access_token: tokens.access_token,
      refreshToken: tokens.refresh_token,
    };
    try {
      Cookies.set("access_token", loginPayload.access_token, {
        expires: 30,
        path: "/",
      });

      Cookies.set("refreshToken", loginPayload.refreshToken, {
        expires: 30,
        path: "/",
      });
      await dispatch(fetchUser()).unwrap();
      dispatch(fetchRoles());
      dispatch(loginSuccess(loginPayload));
      posthog.capture("user_logged_in");

      return loginPayload;
    } catch (error: unknown) {
      Cookies.remove("access_token", { path: "/" });
      Cookies.remove("refreshToken", { path: "/" });
      dispatch(
        loginFailure(
          error instanceof Error ? error.message : "Could not complete sign in."
        )
      );
      return rejectWithValue(error);
    }
  }
);

export const loginUser = createAsyncThunk(
  "/auth/login",
  async (credentials: LoginCredentials, { dispatch }) => {
    try {
      dispatch(loginStart());

      const response: AxiosResponse<LoginResponse> = await authService.login(
        credentials
      );

      const loginPayload = {
        access_token: response.data.access_token,
        refreshToken: response.data.refresh_token,
      };

      Cookies.set("access_token", loginPayload.access_token, {
        expires: 30,
        path: "/",
      });

      Cookies.set("refreshToken", loginPayload.refreshToken, {
        expires: 30,
        path: "/",
      });

      dispatch(loginSuccess(loginPayload));
      const token = Cookies.get("access_token");
      if (token) {
        dispatch(fetchUser());
        dispatch(fetchRoles());
      }

      return loginPayload;
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        console.error("Axios Error: ", error.response?.data);
        dispatch(loginFailure(error.response?.data?.message));
      } else {
        console.error("Unknown Error: ", error);
        dispatch(loginFailure("An unknown error occurred."));
      }
      throw error;
    }
  }
);

export const refreshAccessToken = createAsyncThunk(
  "auth/refreshToken",
  async (_, { dispatch }) => {
    try {
      const refreshToken = Cookies.get("refreshToken");
      const access_token = Cookies.get("access_token");

      if (!refreshToken) {
        throw new Error("No refresh token available");
      }

      const response = await authService.refreshToken({
        access_token: access_token ?? "",
        refresh_token: refreshToken,
      });

      const loginPayload = {
        access_token: response.data.access_token,
        refreshToken: response.data.refresh_token,
      };

      Cookies.set("access_token", loginPayload.access_token, {
        expires: 7,
        path: "/",
      });

      Cookies.set("refreshToken", loginPayload.refreshToken, {
        expires: 7,
        path: "/",
      });
      dispatch(loginSuccess(loginPayload));
      return loginPayload;
    } catch (error) {
      localStorage.clear();
      Cookies.remove("access_token", { path: "/" });
      Cookies.remove("refreshToken", { path: "/" });

      throw error;
    }
  }
);
